// src/components/AdminGroups.js
import { useState, useEffect } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, FormControl,
  IconButton, InputLabel, MenuItem, Paper, Select, Stack,
  TextField, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import * as usersService from "../services/usersService";
import {
  useGroupsQuery, useMembershipsQuery,
  useCreateGroup, useRenameGroup, useDeleteGroup,
  useAddMember, useRemoveMember, useSetLeader,
} from "../hooks/useGroups";
import { useRecordAdminAdd, useRecordAdminRemove } from "../hooks/useGroupRequests";
import GoHomeButton from "./GoHomeButton";

export default function AdminGroups({ currentUserId, onNavigateHome }) {
  const { data: groups = [] } = useGroupsQuery();
  const { data: members = [] } = useMembershipsQuery();

  const [users, setUsers] = useState([]);
  useEffect(() => {
    usersService.listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  const createGroupMutation = useCreateGroup();
  const renameGroupMutation = useRenameGroup();
  const deleteGroupMutation = useDeleteGroup();
  const addMemberMutation = useAddMember();
  const removeMemberMutation = useRemoveMember();
  const setLeaderMutation = useSetLeader();
  const recordAdminAddMutation = useRecordAdminAdd();
  const recordAdminRemoveMutation = useRecordAdminRemove();

  const [newGroupName, setNewGroupName] = useState("");
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState("");
  const [addMemberGroupId, setAddMemberGroupId] = useState(null);
  const [addMemberUserId, setAddMemberUserId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await createGroupMutation.mutateAsync({ name: newGroupName.trim(), createdBy: currentUserId });
      setNewGroupName("");
    } catch {
      // Error already logged in groupsService; leave the form as-is so the user can retry.
    }
  };

  const saveGroupName = async () => {
    if (!editingGroupName.trim()) return;
    try {
      await renameGroupMutation.mutateAsync({ groupId: editingGroupId, name: editingGroupName.trim() });
      setEditingGroupId(null);
    } catch {
      // Error already logged in groupsService; leave editing open so the user can retry.
    }
  };

  const deleteGroup = async () => {
    if (!confirmDelete) return;
    try {
      await deleteGroupMutation.mutateAsync({ groupId: confirmDelete.id, name: confirmDelete.name });
      setConfirmDelete(null);
    } catch {
      // Error already logged in groupsService; leave the dialog open so the user can retry.
    }
  };

  const addMember = async () => {
    if (!addMemberGroupId || !addMemberUserId) return;
    const displayName = users.find((u) => u.user_id === addMemberUserId)?.display_name || addMemberUserId;
    try {
      await addMemberMutation.mutateAsync({ groupId: addMemberGroupId, userId: addMemberUserId, displayName });
      await recordAdminAddMutation.mutateAsync({ userId: addMemberUserId, groupId: addMemberGroupId, resolvedBy: currentUserId });
      setAddMemberGroupId(null);
      setAddMemberUserId("");
    } catch {
      // Error already logged in groupsService; leave the form open so the user can retry.
    }
  };

  const removeMember = async () => {
    if (!confirmRemove) return;
    const { groupId, userId } = confirmRemove;
    try {
      await removeMemberMutation.mutateAsync({ groupId, userId });
      await recordAdminRemoveMutation.mutateAsync({ userId, groupId, resolvedBy: currentUserId });
      setConfirmRemove(null);
    } catch {
      // Error already logged in groupsService; leave the dialog open so the user can retry.
    }
  };

  const setLeader = async (groupId, newLeaderId) => {
    try {
      await setLeaderMutation.mutateAsync({ groupId, leaderId: newLeaderId });
    } catch {
      // Error already logged in groupsService.
    }
  };

  const groupMembers = (groupId) => members.filter((m) => m.group_id === groupId);
  const availableToAdd = (groupId) => {
    const current = groupMembers(groupId).map((m) => m.user_id);
    return users.filter((u) => !current.includes(u.user_id));
  };

  return (
    <Paper className="page-card" sx={{ p: { xs: 1.5, sm: 3 }, mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" className="section-header">Admin — Groups</Typography>
        <GoHomeButton onNavigateHome={onNavigateHome} />
      </Stack>

      <Stack spacing={3}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="New Group Name"
            size="small"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={createGroup}
            disabled={!newGroupName.trim()}
            sx={{ minWidth: 160 }}
          >
            Create Group
          </Button>
        </Stack>

        <Divider />

        {groups.map((group) => (
          <Paper key={group.id} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={editingGroupId === group.id ? { xs: "column", sm: "row" } : "row"}
              alignItems={editingGroupId === group.id ? { xs: "stretch", sm: "center" } : "center"}
              spacing={1}
              sx={{ mb: 1 }}
            >
              {editingGroupId === group.id ? (
                <>
                  <TextField
                    size="small"
                    value={editingGroupName}
                    onChange={(e) => setEditingGroupName(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="contained" onClick={saveGroupName}>Save</Button>
                    <Button size="small" onClick={() => setEditingGroupId(null)}>Cancel</Button>
                  </Stack>
                </>
              ) : (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>{group.name}</Typography>
                  <IconButton
                    size="small"
                    aria-label="edit"
                    onClick={() => { setEditingGroupId(group.id); setEditingGroupName(group.name); }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="delete"
                    onClick={() => setConfirmDelete({ id: group.id, name: group.name })}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              {groupMembers(group.id).map((m) => {
                const user = users.find((u) => u.user_id === m.user_id);
                return (
                  <Chip
                    key={m.user_id}
                    label={user?.display_name || m.user_id}
                    size="small"
                    onDelete={() => setConfirmRemove({ groupId: group.id, userId: m.user_id })}
                  />
                );
              })}
            </Box>

            <FormControl size="small" sx={{ minWidth: 200, width: { xs: "100%", sm: 200 }, mb: 1 }} disabled={groupMembers(group.id).length === 0}>
              <InputLabel>Leader</InputLabel>
              <Select
                label="Leader"
                value={group.leader_id ?? ""}
                onChange={(e) => setLeader(group.id, e.target.value)}
              >
                {groupMembers(group.id).map((m) => {
                  const user = users.find((u) => u.user_id === m.user_id);
                  return (
                    <MenuItem key={m.user_id} value={m.user_id}>{user?.display_name || m.user_id}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {addMemberGroupId === group.id ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                <FormControl size="small" sx={{ minWidth: 160, width: { xs: "100%", sm: 160 } }}>
                  <InputLabel>Add user</InputLabel>
                  <Select
                    label="Add user"
                    value={addMemberUserId}
                    onChange={(e) => setAddMemberUserId(e.target.value)}
                  >
                    {availableToAdd(group.id).map((u) => (
                      <MenuItem key={u.user_id} value={u.user_id}>{u.display_name || u.user_id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" disabled={!addMemberUserId} onClick={addMember}>Add</Button>
                  <Button size="small" onClick={() => { setAddMemberGroupId(null); setAddMemberUserId(""); }}>Cancel</Button>
                </Stack>
              </Stack>
            ) : (
              <Button
                size="small"
                variant="outlined"
                disabled={availableToAdd(group.id).length === 0}
                onClick={() => setAddMemberGroupId(group.id)}
              >
                + Add Member
              </Button>
            )}
          </Paper>
        ))}
      </Stack>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete "{confirmDelete?.name}"? All members will be removed. Any user whose primary
            group was this group will need to select a new one on next login.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" onClick={deleteGroup}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRemove} onClose={() => setConfirmRemove(null)}>
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove "{confirmRemove?.userId}" from this group? If this is their primary group,
            they will be prompted to select a new one on next login.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button color="error" onClick={removeMember}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
